<template>
  <label class="ux-field-title" v-if="showLabel"
    >{{ label }}
    <span class="ux-required-marker" v-if="required">*</span></label
  >
  <div class="ux-field-description" v-if="showDescription">
    {{ description }}
  </div>

  <div v-if="multiValue">
    <flex-row class="ux-text-row" :key="index" v-for="(entry, index) in model">
      <flex-cell>
        <datepicker
          :date-only="dateOnly"
          :min-date="minDate"
          :max-date="maxDate"
          :model-value="model[index]"
          @update:model-value="updateMultiValue(index, $event)"
          @focus="touch"
          class="ux-field-focus"
        />
      </flex-cell>
      <flex-cell shrink vcenter>
        <ux-button
          color="default"
          tag="a"
          icon
          v-if="canRemoveValue"
          @click="remove(entry)"
        >
          <ux-icon icon="fa-times" />
        </ux-button>
      </flex-cell>
    </flex-row>
    <ux-button v-if="canAddValue" @click="add()">{{ addLabel }}</ux-button>
  </div>
  <template v-else>
    <datepicker
      :date-only="dateOnly"
      :min-date="minDate"
      :max-date="maxDate"
      v-model="singleModel"
      @focus="touch"
      class="ux-field-focus"
    />
  </template>
</template>
<script>
import InputMixin from "./input-mixin";
import Datepicker from "./datepicker.vue";

//////////////////////////

function zero(input) {
  if (String(input).length < 2) {
    input = `0${input}`;
  }
  return input;
}

//////////////////////////

export default {
  components: {
    Datepicker,
  },
  props: {
    modelValue: {
      type: [Date, String, Array],
    },
    disableTime: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [InputMixin],
  computed: {
    formattedMinDate() {
      if (!this.minDate) return null;
      if (this.showTime) {
        return this.formatDateTime(this.minDate);
      }
      return this.formatDate(this.minDate);
    },
    formattedMaxDate() {
      if (!this.maxDate) return null;
      if (this.showTime) {
        return this.formatDateTime(this.maxDate);
      }
      return this.formatDate(this.maxDate);
    },
    singleModel: {
      get() {
        return this.cleanOutput(this.value);
      },
      set(newValue) {
        if (!newValue) {
          this.value = undefined;
          this.dispatch();
          return;
        }

        const date = new Date(newValue);
        if (isNaN(date.getTime())) {
          return;
        }

        this.value = date.toISOString();
        this.dispatch();
      },
    },
    dateOnlyModel: {
      get() {
        let val = this.cleanOutput(this.model);

        if (this.multiValue) {
          val = val
            .map(function (d) {
              if (!d) {
                return d;
              }

              const date = new Date(d);
              if (isNaN(date.getTime())) {
                return null; // Return null for invalid dates
              }
              return date.toISOString().slice(0, 10);
            })
            .filter(Boolean); // Remove null values
        } else {
          if (val) {
            const date = new Date(val);
            if (isNaN(date.getTime())) {
              return null; // Return null for invalid dates
            }
            val = date.toISOString().slice(0, 10);
          }
        }

        return val;
      },
      set(newValue) {
        if (!newValue) {
          this.value = undefined;
          this.dispatch();
          return;
        }

        //Invalid date so no change here
        if (newValue === "invalid") {
          return;
        }

        ////////////////////////

        var val = this.cleanOutput(newValue);
        var existing = this.cleanOutput(this.value);

        if (val != existing) {
          newValue = this.cleanInput(val);

          if (this.multiValue) {
            newValue = newValue
              .map(function (d) {
                const date = new Date(d);
                if (isNaN(date.getTime())) {
                  return null; // Return null for invalid dates
                }
                return date.toISOString();
              })
              .filter(Boolean); // Remove null values
          } else {
            const date = new Date(newValue);
            if (isNaN(date.getTime())) {
              return; // Don't update if date is invalid
            }
            newValue = date.toISOString();
          }

          this.value = newValue;
          this.dispatch();
        }
      },
    },
    showTime() {
      return !this.dateOnly;
    },
    dateOnly() {
      if (this.disableTime) {
        return true;
      }

      return !!this.field?.dateOnly;
    },
    model: {
      get() {
        let val = this.cleanOutput(this.value);
        return val;
      },
      set(newValue) {
        if (!newValue) {
          this.value = undefined;
          this.dispatch();
          return;
        }

        //Invalid date so no change here
        if (newValue === "invalid") {
          return;
        }

        ////////////////////////

        var val = this.cleanOutput(newValue);
        var existing = this.cleanOutput(this.value);

        if (val != existing) {
          newValue = this.cleanInput(val);
          this.value = newValue;
          this.dispatch();
        }
      },
    },
  },
  methods: {
    updateMultiValue(index, newValue) {
      if (!newValue) {
        return;
      }

      const date = new Date(newValue);
      if (isNaN(date.getTime())) {
        return;
      }

      // Clone the model array and update the specific index
      const updated = [...this.model];
      updated[index] = date.toISOString();
      this.value = this.cleanInput(updated);
      this.dispatch();
    },
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      return `${d.getFullYear()}-${zero(d.getMonth() + 1)}-${zero(d.getDate())}`;
    },
    formatDateTime(date) {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      return `${this.formatDate(d)}T${zero(d.getHours())}:${zero(d.getMinutes())}`;
    },
    cleanOutputValue(d) {
      d = d ? new Date(d) : new Date();

      if (isNaN(d.getFullYear())) {
        return;
      }

      return `${d.getFullYear()}-${zero(d.getMonth() + 1)}-${zero(d.getDate())}T${zero(d.getHours())}:${zero(d.getMinutes())}`;
    },
    cleanInputValue(d) {
      let dt = new Date(d);
      if (dt.toString() == "Invalid Date") {
        return "invalid";
      }
      return dt.toISOString();
    },
    getNewDefaultEntry() {
      return new Date();
    },
  },
};
</script>
<style lang="scss" scoped>
.ux-text-row {
  margin-bottom: 0.5em;
}

.ux-text-row .ux-btn {
  margin-left: 0.25em;
}
</style>
